import lockIcon from "../../medias/lockIcon.svg";
import { Container, Typography } from "@mui/material";
import { 
    InputButton,
    InputField,
    showSimpleSnackbarDispatcher,
    showSnackbar,
} from "../../components/";
import { Loading } from "../Loading";
import { Page } from "../Page";
import { apiClient } from "../../api/";
import { styles } from "../../theme/";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "../../hooks/";

// Interfaces
interface NewPasswordProps {
    to?: string;
}

/**
 * NewPassword page
 */
export const NewPassword = ({ to }: NewPasswordProps) => {
    // Hooks
    const navigate = useNavigate();
    const SearchParams = useSearchParams();

    // useStates
    const [error, setError] = useState<boolean>(false);
    const [expirationDatetime, setExpirationDatetime] = useState<string | undefined>();
    const [loading, setLoading] = useState<boolean>(true);
    const [password, setPassword] = useState<string>("");

    const token = SearchParams.get("token");

    // useMemos
    const localToken = useMemo(() => localStorage.getItem("token"), []);

    // Handlers
    const handleUpdatePassword = async () => {
        try {
            const [response, statusOk] = await apiClient.updatePassword(password, token ?? localToken ?? "");

            if (statusOk) {
                showSnackbar({ message: `Your password was updated.`, variant: "success" });
                navigate(to ? to : "/signIn");
            } else {
                showSimpleSnackbarDispatcher(response as { [key: string]: string[]; });
            }

        } catch (error) {
            setError(true);
            showSnackbar({ message: String(error), variant: "error" });
        }
    };

    // useEffects
    useEffect(() => {
        const getPasswordResetRequest = async (token: string) => {
            try {
                const [response, statusOk] = await apiClient.validatePasswordResetRequest(token);

                if (statusOk && response.expiration_datetime) {
                    setExpirationDatetime(response.expiration_datetime);
                } else {
                    showSimpleSnackbarDispatcher(response as { [key: string]: string[]; });
                    navigate("/signIn");
                }
            } catch (error) {
                setError(true);
                showSnackbar({ message: String(error), variant: "error" });
            } finally {
                await new Promise(resolve => setTimeout(resolve, 1000));
                setLoading(false);
            }
        };

        if (!token && !localToken) {
            showSnackbar({ message: "Token may not be blank.", variant: "error" });
            navigate("/signIn");
        } else {
            if (!localToken && token) {
                getPasswordResetRequest(token);
            } else {
                showSnackbar({ message: `Insert your new password!`, variant: "success" });
                // TODO should have await new Promise(resolve => setTimeout(resolve, 1000));
                setLoading(false);
            }
        }
    }, [token, localToken, navigate]);

    useEffect(() => {
        if (!expirationDatetime) return;

        const now = new Date().getTime();
        const expiration = new Date(expirationDatetime).getTime();
        const isExpired = (expiration - now) < 0;

        if (isExpired) {
            showSnackbar({ message: `Your reset password request has expired.`, variant: "error" });
            navigate("/resetPassword");
        } else {
            showSnackbar({ message: `Insert your new password!`, variant: "success" });
        }
    }, [expirationDatetime, navigate]);

    return (
        <Page menu>
            <Loading show={loading} />
            <Container style={styles.inPlaceModal} >
                <Typography style={{ fontFamily: "ArchivoBlack", fontSize: 36 }} >New Password</Typography>
                <InputField type="password" value={password} leftIcon={lockIcon} leftIconSize="100%" placeholder={"New password"} error={error} setValue={setPassword} submitAction={() => handleUpdatePassword()} />
                <InputButton type="secondary" onClick={() => handleUpdatePassword()} tooltip="Change password">Confirm</InputButton>
            </Container>
        </Page>
    );
};